/* TREKCAMP branded mobile Sort By for Collections + Search.
   This is presentation and event delegation only; Shopify facets.js owns
   filtering, URL state, AJAX re-rendering and SearchUpdateEvent dispatch. */
const pickerSelector = '#MainContent[data-template^="collection"] [data-tc-sort-picker], #MainContent[data-template^="search"] [data-tc-sort-picker]';
const allPickers = () => document.querySelectorAll(pickerSelector);

/* The desktop indicator mirrors the mobile Sort By dot: only a non-default
   explicit sort_by value counts. The Shopify results sort without a query
   parameter is never treated as a manual sort change. */
function syncDesktopSortDots() {
  const requested = new URL(window.location.href).searchParams.get('sort_by');
  document.querySelectorAll(
    '#MainContent[data-template^="collection"] .sorting-filter.tc-sort-collection, ' +
    '#MainContent[data-template^="search"] .sorting-filter.tc-sort-search'
  ).forEach(sorter => {
    const details = sorter.querySelector('details[data-default-sort-by]');
    const dot = sorter.querySelector('.tc-sort-desktop-dot');
    if (!(details instanceof HTMLDetailsElement) || !(dot instanceof HTMLElement)) return;
    const defaultSort = details.dataset.defaultSortBy || '';
    dot.hidden = !requested || requested === defaultSort;
  });
}

function closePicker(picker, restoreFocus = false) {
  if (!(picker instanceof HTMLElement)) return;
  const panel = picker.querySelector('[data-tc-sort-panel]');
  const trigger = picker.querySelector('[data-tc-sort-trigger]');
  if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLButtonElement)) return;
  panel.hidden = true;
  picker.removeAttribute('data-open');
  trigger.setAttribute('aria-expanded', 'false');
  if (restoreFocus) trigger.focus({ preventScroll: true });
}

function openPicker(picker) {
  if (!(picker instanceof HTMLElement)) return;
  for (const other of allPickers()) if (other !== picker) closePicker(other);
  const panel = picker.querySelector('[data-tc-sort-panel]');
  const trigger = picker.querySelector('[data-tc-sort-trigger]');
  if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLButtonElement)) return;
  panel.hidden = false;
  picker.setAttribute('data-open', '');
  trigger.setAttribute('aria-expanded', 'true');
  const selected = panel.querySelector('[data-tc-sort-option][aria-selected="true"]');
  const first = panel.querySelector('[data-tc-sort-option]');
  const focusTarget = selected || first;
  if (focusTarget instanceof HTMLElement) {
    focusTarget.tabIndex = 0;
    focusTarget.focus({ preventScroll: true });
  }
}

function focusOption(panel, direction) {
  const options = Array.from(panel.querySelectorAll('[data-tc-sort-option]'));
  if (!options.length) return;
  const currentIndex = options.indexOf(document.activeElement);
  const next = direction === 'first' ? 0 : direction === 'last' ? options.length - 1
    : (currentIndex + (direction === 'next' ? 1 : -1) + options.length) % options.length;
  options.forEach(option => { option.tabIndex = -1; });
  const target = options[next];
  if (target instanceof HTMLElement) {
    target.tabIndex = 0;
    target.focus({ preventScroll: true });
  }
}

function syncPickerState() {
  syncDesktopSortDots();
  const requested = new URL(window.location.href).searchParams.get('sort_by');
  for (const picker of allPickers()) {
    const options = [...picker.querySelectorAll('[data-tc-sort-option]')];
    if (!options.length) continue;
    const originalDefault = picker.dataset.defaultSortBy || '';
    const chosen = options.find(option => option.dataset.sortValue === requested)
      || options.find(option => option.dataset.sortValue === originalDefault)
      || options.find(option => option.getAttribute('aria-selected') === 'true')
      || options[0];
    for (const option of options) {
      const active = option === chosen;
      option.setAttribute('aria-selected', active ? 'true' : 'false');
      option.tabIndex = active ? 0 : -1;
    }
    const dot = picker.querySelector('.tc-sort-active-dot');
    if (dot instanceof HTMLElement) dot.hidden = !requested || requested === originalDefault;
  }
}

document.addEventListener('click', event => {
  if (!(event.target instanceof Element)) return;
  const trigger = event.target.closest('[data-tc-sort-trigger]');
  if (trigger instanceof HTMLButtonElement) {
    const picker = trigger.closest('[data-tc-sort-picker]');
    if (!(picker instanceof HTMLElement)) return;
    if (picker.hasAttribute('data-open')) closePicker(picker, true);
    else {
      syncPickerState();
      openPicker(picker);
    }
    return;
  }
  const option = event.target.closest('[data-tc-sort-option]');
  if (option instanceof HTMLButtonElement) {
    const picker = option.closest('[data-tc-sort-picker]');
    if (!(picker instanceof HTMLElement)) return;
    const value = option.dataset.sortValue;
    if (!value) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    closePicker(picker, true);
    if (url.href === window.location.href) return;
    const main = picker.closest('#MainContent');
    const facets = main?.querySelector('#filters-drawer facets-form-component')
      || main?.querySelector('facets-form-component');
    if (facets && typeof facets.updateFiltersByURL === 'function') {
      facets.updateFiltersByURL(url.href);
    } else {
      window.location.assign(url.href);
    }
    syncPickerState();
    return;
  }
  for (const picker of allPickers()) {
    if (!picker.contains(event.target)) closePicker(picker);
  }
});

document.addEventListener('keydown', event => {
  if (!(event.target instanceof Element)) return;
  const trigger = event.target.closest('[data-tc-sort-trigger]');
  if (trigger && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
    event.preventDefault();
    const picker = trigger.closest('[data-tc-sort-picker]');
    if (picker instanceof HTMLElement) openPicker(picker);
    return;
  }
  const option = event.target.closest('[data-tc-sort-option]');
  if (option) {
    const panel = option.closest('[data-tc-sort-panel]');
    if (!(panel instanceof HTMLElement)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closePicker(option.closest('[data-tc-sort-picker]'), true);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault(); focusOption(panel, 'next');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault(); focusOption(panel, 'previous');
    } else if (event.key === 'Home') {
      event.preventDefault(); focusOption(panel, 'first');
    } else if (event.key === 'End') {
      event.preventDefault(); focusOption(panel, 'last');
    } else if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault(); option.click();
    }
    return;
  }
  if (event.key === 'Escape') {
    for (const picker of allPickers()) if (picker.hasAttribute('data-open')) closePicker(picker, true);
  }
});

document.addEventListener('focusin', event => {
  if (!(event.target instanceof Node)) return;
  for (const picker of allPickers()) {
    if (picker.hasAttribute('data-open') && !picker.contains(event.target)) closePicker(picker);
  }
});

window.addEventListener('resize', () => {
  if (window.innerWidth >= 750) for (const picker of allPickers()) closePicker(picker);
});
window.addEventListener('popstate', () => {
  for (const picker of allPickers()) closePicker(picker);
  syncPickerState();
});
window.addEventListener('pageshow', syncPickerState);
document.addEventListener('trekcamp:collection-results-rendered', syncPickerState);

/* AJAX search results replace the facet toolbar without always raising the
   collection-only event. One batched observation keeps both dots in sync. */
let sortRefreshQueued = false;
function watchSortToolbar() {
  const root = document.querySelector('#MainContent[data-template^="collection"], #MainContent[data-template^="search"]');
  if (!root) return;
  const observer = new MutationObserver(() => {
    if (sortRefreshQueued) return;
    sortRefreshQueued = true;
    requestAnimationFrame(() => {
      sortRefreshQueued = false;
      syncPickerState();
    });
  });
  observer.observe(root, { childList: true, subtree: true });
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchSortToolbar, { once: true });
} else {
  watchSortToolbar();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncPickerState, { once: true });
} else {
  syncPickerState();
}
